import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { lastValueFrom, take } from 'rxjs';
import { State } from 'src/app/core/state/core.state';
import * as fromContext from 'src/app/core/state/context';
import * as fromPath from 'src/app/core/state/path';
import * as fromSubscriber from 'src/app/core/state/subscriber';
import { environment } from 'src/environments/environment';
import { EncryptionService } from './encryption.service';
import { GtmService } from './google-tag-manager.service';

export type GtmTrackedEvent =
  | 'price_view'
  | 'form_devis_submit'
  | 'contract_signature_submit'
  | 'funnel_step';

export type GtmEventArguments =
  | [eventName: Exclude<GtmTrackedEvent, 'funnel_step'>]
  | [eventName: 'funnel_step', data: { step_name: string }];

@Injectable({
  providedIn: 'root'
})
export class GtmEventServiceService {
  constructor(
    private store: Store<State>,
    private gtmService: GtmService,
    private encryptionService: EncryptionService
  ) {}

  async pushEvent(...[eventName, data]: GtmEventArguments): Promise<void> {
    const path = await lastValueFrom(this.store.select(fromPath.selectPathType).pipe(take(1)));
    const offer = path + '_' + environment.brand;
    const context = await lastValueFrom(
      this.store.select(fromContext.selectContextData).pipe(take(1))
    );
    const partnerCode = context?.partnerCode ?? 'VYV';

    let eventData: any;

    switch (eventName) {
      case 'price_view':
      case 'form_devis_submit':
      case 'contract_signature_submit':
        const { hashedEmail, phonePrefix } = await this.buildUserData();
        eventData = {
          user_data_em: hashedEmail,
          user_data_ph: phonePrefix,
          form_name: offer,
          partner_code: partnerCode
        };
        break;

      case 'funnel_step':
        eventData = {
          form_name: offer,
          partner_code: partnerCode,
          step_name: data.step_name
        };
        break;
    }

    this.gtmService.pushEvent(eventName, eventData);
  }

  private async buildUserData(): Promise<any> {
    const subscriber = await lastValueFrom(
      this.store.select(fromSubscriber.selectSubscriber).pipe(take(1))
    );
    const email = subscriber.subscriber?.Email ?? '';
    const hashedEmail = email ? await this.encryptionService.sha256Hex(email) : '';
    const phonePrefix =
      subscriber.subscriber?.Telephone !== undefined && subscriber.subscriber?.Telephone !== ''
        ? (subscriber.subscriber?.Indicatif ?? '') + subscriber.subscriber?.Telephone
        : '';
    return { hashedEmail, phonePrefix };
  }
}
